// BRK-S045 — the unified submission flow spine. A submission moves through six
// steps; the ACTIVE step is derived purely from server state (account
// completeness, effective date, presence of indications/quotes) and is never
// stored client-side (Non-Negotiable #9). Both /submissions/new and
// /submissions/:id render the same stepper off this derivation so the intake →
// profile → rating → quoting path reads as one continuous flow.

export type SubmissionFlowStep = 1 | 2 | 3 | 4 | 5 | 6;

export interface FlowStepDef {
  step: SubmissionFlowStep;
  label: string;
  /** Step 2 (insured profile) is only active when the account floor isn't met;
   *  a renewal off a complete account skips straight past it. */
  conditional?: boolean;
}

/** The six flow steps, in order. Step 1 lives on /submissions/new (pre-ID); the
 *  rest are reached on /submissions/:id. */
export const FLOW_STEPS: FlowStepDef[] = [
  { step: 1, label: "New / Renewal" },
  { step: 2, label: "Insured profile", conditional: true },
  { step: 3, label: "Rating details" },
  { step: 4, label: "Price indications" },
  { step: 5, label: "Firm quote" },
  { step: 6, label: "Compare & bind" },
];

/** The server-derived signals that place a submission on the flow. */
export interface SubmissionFlowState {
  /** Is the insured account complete (the required floor met — not a draft)? */
  accountComplete: boolean;
  /** Is the submission rated (has an effective date, so it's quote-ready)? */
  rated: boolean;
  /** Has at least one price indication been produced? */
  hasIndications: boolean;
  /** Has at least one firm (bindable) quote been produced? */
  hasQuotes: boolean;
}

/**
 * Map server state to the single active step. Precedence flows down the funnel:
 * an incomplete account pins to step 2 regardless of anything downstream (the
 * gate protects every entry path — BRK-S046), then rating, then indications,
 * then a firm quote, then compare & bind.
 */
export function deriveFlowStep(state: SubmissionFlowState): SubmissionFlowStep {
  if (!state.accountComplete) return 2;
  if (!state.rated) return 3;
  if (!state.hasIndications) return 4;
  if (!state.hasQuotes) return 5;
  return 6;
}

// ─── Phases (BRK-S058) ────────────────────────────────────────────────────────
// The six steps read as six disjoint screens even though 4–6 all happen on the
// one pricing view. Grouping them into three broker-meaningful PHASES fixes that
// without changing the derivation: a phase is just an ordered set of the same
// steps, so the active phase (and everything below) stays a pure function of the
// server-derived step (NN #9). The stepper renders the phase spine + the active
// phase's sub-steps + an honest milestone; all three derive from `current`.

export type FlowPhaseKey = "setup" | "shop" | "firm";

export interface FlowPhaseDef {
  key: FlowPhaseKey;
  label: string;
  /** The flow steps this phase groups, in order. */
  steps: SubmissionFlowStep[];
}

/** The three phases, in order. "Set up" is everything to become quote-ready
 *  (intake → insured profile → rating), "Shop the market" is the price
 *  indications fan-out, "Firm up & bind" is the firm quote + compare/bind. */
export const FLOW_PHASES: FlowPhaseDef[] = [
  { key: "setup", label: "Set up", steps: [1, 2, 3] },
  { key: "shop", label: "Shop the market", steps: [4] },
  { key: "firm", label: "Firm up & bind", steps: [5, 6] },
];

/** The index of the phase a step belongs to (0-based, always found). */
export function phaseIndexOfStep(step: SubmissionFlowStep): number {
  return FLOW_PHASES.findIndex((p) => (p.steps as number[]).includes(step));
}

/**
 * An honest "how much is left" line, derived purely from the current step — no
 * fabricated minutes (we have no historical timing data). It counts flow steps
 * to the next meaningful milestone: the broker's first price indication (the
 * payoff), then a firm quote. Phrased as remaining STEPS, so it can never
 * over-promise a clock it can't keep.
 */
export function milestoneText(current: SubmissionFlowStep): string {
  const toIndication = 4 - current;
  if (toIndication > 0) {
    return `${toIndication} step${toIndication > 1 ? "s" : ""} to your first price indication`;
  }
  if (current === 4) return "Getting your first price indication";
  const toQuote = 6 - current; // 5 → 1, 6 → 0
  if (toQuote > 0) {
    return `${toQuote} step${toQuote > 1 ? "s" : ""} to a firm quote`;
  }
  return "Compare offers and bind";
}
